import { useEffect, useState } from "react";

/**
 * Lazy YouTube background. The iframe (and the ~500 KB of YouTube
 * player JS it pulls in) is only mounted after the page is interactive
 * — keeps it off the LCP critical path while still showing a moving
 * background within ~half a second of paint.
 */
export default function DeferredYouTubeBg({
  videoId,
  title,
  delayMs = 600,
}: {
  videoId: string;
  title?: string;
  delayMs?: number;
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    if (typeof ric === "function") {
      const id = ric(() => setMounted(true), { timeout: delayMs + 400 });
      return () => {
        const cic = (window as unknown as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
        if (typeof cic === "function") cic(id);
      };
    }
    const t = window.setTimeout(() => setMounted(true), delayMs);
    return () => window.clearTimeout(t);
  }, [delayMs]);

  if (!mounted) return null;

  const params = new URLSearchParams({
    autoplay: "1",
    mute: "1",
    loop: "1",
    playlist: videoId,
    controls: "0",
    showinfo: "0",
    modestbranding: "1",
    iv_load_policy: "3",
    rel: "0",
    disablekb: "1",
    playsinline: "1",
  });

  return (
    <iframe
      title={title || "Background video"}
      src={`https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`}
      allow="autoplay; encrypted-media; picture-in-picture"
      // Scaled to cover the parent: the wrapper's overflow-hidden crops
      // the excess so the visible window is the centred 16:9 frame.
      // 177.78vh = 100vh × 16/9, 56.25vw = 100vw × 9/16.
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      style={{ width: "max(100%, 177.78vh)", height: "max(100%, 56.25vw)" }}
    />
  );
}
